import { faSync, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { Badge, Button, Input, Table } from "reactstrap";
import {
  ApiSendGridInvoicesPayload,
  ApiSendGridInvoice,
  emailMarketingApiService,
} from "../../cores/emailMarketing/services/api";
import { formatUsd } from "./sendgridMarketingPricing";
import {
  readSendGridInvoicesLocalCache,
  writeSendGridInvoicesLocalCache,
} from "./sendgridInvoicesLocalCache";

type Props = {
  open: boolean;
  onClose: () => void;
};

function formatInvoiceAmount(
  amount: number | string | null,
  currency: string
): string {
  if (amount == null || amount === "") return "—";
  const num = Number(amount);
  if (Number.isNaN(num)) return String(amount);
  if (currency === "USD") return formatUsd(num);
  return `${num.toLocaleString()} ${currency}`;
}

function formatInvoiceDate(value: string | null): string {
  if (!value) return "—";
  const m = moment(value);
  return m.isValid() ? m.format("DD/MM/YYYY") : value;
}

function toNumber(value: number | string | null | undefined): number {
  if (value == null || value === "") return 0;
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

function invoiceDateMoment(inv: ApiSendGridInvoice): moment.Moment {
  return moment(inv.invoiceDate || inv.paidAt);
}

function extractApiError(error: unknown): string {
  const e = error as {
    response?: { data?: { message?: string; error?: string } };
    message?: string;
  };
  return (
    e.response?.data?.message ||
    e.response?.data?.error ||
    e.message ||
    "Error desconocido"
  );
}

function isStaleSyncError(message: string | null | undefined): boolean {
  if (!message) return true;
  return /^(not found|404)$/i.test(message.trim());
}

export default function SendGridInvoicesPanel({ open, onClose }: Props) {
  const intl = useIntl();
  const [data, setData] = useState<ApiSendGridInvoicesPayload | null>(null);
  const [loadingCache, setLoadingCache] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const applyPayload = useCallback((payload: ApiSendGridInvoicesPayload) => {
    setData(payload);
    writeSendGridInvoicesLocalCache(payload);
  }, []);

  const loadCached = useCallback(async () => {
    const local = readSendGridInvoicesLocalCache();
    if (local?.invoices?.length) {
      setData(local);
    }

    setLoadingCache(true);
    try {
      const payload = await emailMarketingApiService.getAdminBillingInvoices();
      applyPayload(payload);
    } catch (e) {
      console.error(e);
      if (!local) {
        setData({
          invoices: [],
          syncedAt: null,
          fingerprint: null,
          fromCache: true,
          changed: false,
          lastSyncError: intl.formatMessage({
            id: "app.sendgridBilling.invoices.loadError",
          }),
          billingApiAvailable: null,
          needsBillingKey: false,
          totalPaid: 0,
        });
      }
    } finally {
      setLoadingCache(false);
    }
  }, [applyPayload, intl]);

  const syncInvoices = useCallback(async () => {
    setSyncing(true);
    try {
      const payload = await emailMarketingApiService.syncAdminBillingInvoices();
      applyPayload(payload);
    } catch (e) {
      console.error(e);
      setData((prev) =>
        prev
          ? {
              ...prev,
              lastSyncError: extractApiError(e),
              needsBillingKey:
                prev.needsBillingKey ||
                /insufficient permissions|billing access|forbidden/i.test(
                  extractApiError(e)
                ),
              billingApiAvailable:
                /insufficient permissions|billing access|forbidden/i.test(
                  extractApiError(e)
                )
                  ? false
                  : prev.billingApiAvailable,
            }
          : {
              invoices: [],
              syncedAt: null,
              fingerprint: null,
              fromCache: false,
              changed: false,
              lastSyncError: extractApiError(e),
              billingApiAvailable: /insufficient permissions|billing access|forbidden/i.test(
                extractApiError(e)
              )
                ? false
                : null,
              needsBillingKey: /insufficient permissions|billing access|forbidden/i.test(
                extractApiError(e)
              ),
              totalPaid: 0,
            }
      );
    } finally {
      setSyncing(false);
    }
  }, [applyPayload]);

  useEffect(() => {
    if (!open) return;
    loadCached();
  }, [open, loadCached]);

  const invoices = data?.invoices || [];

  const filteredInvoices = useMemo(() => {
    return invoices.filter((inv) => {
      const invoiceMoment = invoiceDateMoment(inv);
      if (!invoiceMoment.isValid()) {
        return !dateFrom && !dateTo;
      }
      if (dateFrom && invoiceMoment.isBefore(moment(dateFrom), "day")) {
        return false;
      }
      if (dateTo && invoiceMoment.isAfter(moment(dateTo), "day")) {
        return false;
      }
      return true;
    });
  }, [invoices, dateFrom, dateTo]);

  const isDateFilterActive = Boolean(dateFrom || dateTo);

  const invoiceMetrics = useMemo(() => {
    const source = filteredInvoices;
    const totalPaid = source.reduce((sum, inv) => sum + toNumber(inv.amount), 0);
    const averageInvoice = source.length ? totalPaid / source.length : 0;
    const totalOutstanding = source.reduce(
      (sum, inv) => sum + toNumber(inv.unpaidBalance),
      0
    );
    const pdfReady = source.filter(
      (inv) => String(inv.pdfStatus || "").toLowerCase() === "generated"
    ).length;
    const latestInvoice = source.reduce<ApiSendGridInvoice | null>(
      (latest, inv) => {
        if (!latest) return inv;
        const currentDate = inv.invoiceDate || inv.paidAt || "";
        const latestDate = latest.invoiceDate || latest.paidAt || "";
        return currentDate > latestDate ? inv : latest;
      },
      null
    );
    const currentYearTotal = source.reduce((sum, inv) => {
      const invoiceYear = moment(inv.invoiceDate || inv.paidAt).year();
      return invoiceYear === moment().year() ? sum + toNumber(inv.amount) : sum;
    }, 0);

    return {
      totalPaid,
      averageInvoice,
      totalOutstanding,
      pdfReady,
      latestInvoice,
      currentYearTotal,
    };
  }, [filteredInvoices]);

  const showBillingKeyHint =
    invoices.length === 0 &&
    (data?.needsBillingKey ||
      data?.billingApiAvailable === false ||
      /insufficient permissions|billing access|forbidden/i.test(
        data?.lastSyncError || ""
      ) ||
      !data?.syncedAt);

  const showSyncError =
    Boolean(data?.lastSyncError) &&
    !showBillingKeyHint &&
    !isStaleSyncError(data?.lastSyncError) &&
    invoices.length === 0;

  const formatInvoiceStatus = (status: string) => {
    const normalized = status.toLowerCase();
    return intl.formatMessage({
      id: `app.sendgridBilling.invoices.status.${normalized}`,
      defaultMessage: status,
    });
  };

  const formatPdfStatus = (
    pdfStatus: string | null | undefined,
    pdfUrl: string | null | undefined
  ) => {
    if (pdfUrl) {
      return intl.formatMessage({ id: "app.sendgridBilling.invoices.pdf.ready" });
    }
    const normalized = String(pdfStatus || "").toLowerCase();
    if (normalized === "generated") {
      return intl.formatMessage({ id: "app.sendgridBilling.invoices.pdf.generated" });
    }
    if (!pdfStatus) return "—";
    return pdfStatus;
  };

  const applyQuickFilter = (preset: "year" | "quarter" | "all") => {
    if (preset === "all") {
      setDateFrom("");
      setDateTo("");
      return;
    }
    if (preset === "year") {
      setDateFrom(moment().startOf("year").format("YYYY-MM-DD"));
      setDateTo(moment().format("YYYY-MM-DD"));
      return;
    }
    setDateFrom(moment().subtract(3, "months").startOf("month").format("YYYY-MM-DD"));
    setDateTo(moment().format("YYYY-MM-DD"));
  };

  if (!open) return null;

  return (
    <div className="sendgrid-billing">
      <div className="sendgrid-billing__invoices-overlay" role="dialog" aria-modal>
        <div className="sendgrid-billing__invoices-panel">
          <header className="sendgrid-billing__invoices-head">
            <div>
              <h5 className="mb-1">
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.title" })}
              </h5>
              <p className="small text-muted mb-0">
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.subtitle" })}
              </p>
            </div>
            <div className="sendgrid-billing__invoices-actions">
              <Button
                size="sm"
                color="primary"
                onClick={syncInvoices}
                disabled={syncing || loadingCache}
              >
                <FontAwesomeIcon icon={faSync} spin={syncing} className="mr-1" />
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.sync" })}
              </Button>
              <Button size="sm" color="light" onClick={onClose} aria-label="Cerrar">
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          </header>

          <div className="sendgrid-billing__invoices-meta small text-muted">
            {data?.syncedAt
              ? intl.formatMessage(
                  { id: "app.sendgridBilling.invoices.lastSync" },
                  {
                    date: moment(data.syncedAt).format("DD/MM/YYYY HH:mm"),
                    count: invoices.length,
                  }
                )
              : intl.formatMessage({ id: "app.sendgridBilling.invoices.neverSynced" })}
            {data?.changed === false && syncing === false && data?.syncedAt
              ? ` · ${intl.formatMessage({ id: "app.sendgridBilling.invoices.noChanges" })}`
              : null}
            {isDateFilterActive
              ? ` · ${intl.formatMessage(
                  { id: "app.sendgridBilling.invoices.filter.showing" },
                  { shown: filteredInvoices.length, total: invoices.length }
                )}`
              : null}
          </div>

          {showBillingKeyHint ? (
            <div className="sendgrid-billing__invoices-hint">
              {intl.formatMessage({ id: "app.sendgridBilling.invoices.billingKeyHint" })}
            </div>
          ) : null}

          {showSyncError ? (
            <div className="sendgrid-billing__invoices-hint is-warning">
              {data?.lastSyncError}
            </div>
          ) : null}

          <div className="sendgrid-billing__invoices-summary">
            <div className="sendgrid-billing__invoices-total-card">
              <span>
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.kpi.totalPaid" })}
              </span>
              <strong>{formatUsd(invoiceMetrics.totalPaid)}</strong>
              <small>
                {intl.formatMessage(
                  { id: "app.sendgridBilling.invoices.kpi.totalHint" },
                  { count: filteredInvoices.length }
                )}
              </small>
            </div>

            <div className="sendgrid-billing__invoices-kpis">
              <div className="sendgrid-billing__invoices-kpi">
                <span>
                  {intl.formatMessage({
                    id: "app.sendgridBilling.invoices.kpi.currentYear",
                  })}
                </span>
                <strong>{formatUsd(invoiceMetrics.currentYearTotal)}</strong>
              </div>
              <div className="sendgrid-billing__invoices-kpi">
                <span>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.kpi.average" })}
                </span>
                <strong>{formatUsd(invoiceMetrics.averageInvoice)}</strong>
              </div>
              <div className="sendgrid-billing__invoices-kpi">
                <span>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.kpi.latest" })}
                </span>
                <strong>
                  {invoiceMetrics.latestInvoice
                    ? formatInvoiceAmount(
                        invoiceMetrics.latestInvoice.amount,
                        invoiceMetrics.latestInvoice.currency
                      )
                    : "—"}
                </strong>
                <small>
                  {invoiceMetrics.latestInvoice
                    ? formatInvoiceDate(
                        invoiceMetrics.latestInvoice.invoiceDate ||
                          invoiceMetrics.latestInvoice.paidAt
                      )
                    : "—"}
                </small>
              </div>
              <div className="sendgrid-billing__invoices-kpi">
                <span>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.kpi.pdfs" })}
                </span>
                <strong>
                  {invoiceMetrics.pdfReady}/{filteredInvoices.length}
                </strong>
              </div>
              <div className="sendgrid-billing__invoices-kpi">
                <span>
                  {intl.formatMessage({
                    id: "app.sendgridBilling.invoices.kpi.outstanding",
                  })}
                </span>
                <strong>{formatUsd(invoiceMetrics.totalOutstanding)}</strong>
              </div>
            </div>
          </div>

          <div className="sendgrid-billing__invoices-filters">
            <div className="sendgrid-billing__invoices-filter-fields">
              <label className="sendgrid-billing__invoices-filter-field">
                <span>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.filter.from" })}
                </span>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                />
              </label>
              <label className="sendgrid-billing__invoices-filter-field">
                <span>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.filter.to" })}
                </span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                />
              </label>
            </div>
            <div className="sendgrid-billing__invoices-filter-presets">
              <Button
                size="sm"
                color="light"
                className={isDateFilterActive && dateFrom === moment().startOf("year").format("YYYY-MM-DD") ? "active" : ""}
                onClick={() => applyQuickFilter("year")}
              >
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.filter.thisYear" })}
              </Button>
              <Button
                size="sm"
                color="light"
                onClick={() => applyQuickFilter("quarter")}
              >
                {intl.formatMessage({ id: "app.sendgridBilling.invoices.filter.last3Months" })}
              </Button>
              {isDateFilterActive ? (
                <Button size="sm" color="link" onClick={() => applyQuickFilter("all")}>
                  {intl.formatMessage({ id: "app.sendgridBilling.invoices.filter.clear" })}
                </Button>
              ) : null}
            </div>
          </div>

          <div className="sendgrid-billing__invoices-table-wrap">
            <Table
              responsive
              hover
              size="sm"
              className="mb-0 sendgrid-billing__invoices-table"
            >
              <thead>
                <tr>
                  <th>
                    {intl.formatMessage({ id: "app.sendgridBilling.invoices.col.date" })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: "app.sendgridBilling.invoices.col.number" })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: "app.sendgridBilling.invoices.col.amount" })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: "app.sendgridBilling.invoices.col.status" })}
                  </th>
                  <th>
                    {intl.formatMessage({ id: "app.sendgridBilling.invoices.col.pdf" })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      {loadingCache
                        ? intl.formatMessage({
                            id: "app.sendgridBilling.invoices.loading",
                          })
                        : isDateFilterActive
                          ? intl.formatMessage({
                              id: "app.sendgridBilling.invoices.filter.empty",
                            })
                          : intl.formatMessage({
                              id: "app.sendgridBilling.invoices.empty",
                            })}
                    </td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv) => (
                    <tr key={inv.id}>
                      <td>
                        <strong>{formatInvoiceDate(inv.invoiceDate || inv.paidAt)}</strong>
                      </td>
                      <td>
                        <span className="sendgrid-billing__invoice-number">
                          {inv.invoiceNumber}
                        </span>
                      </td>
                      <td>
                        <strong>{formatInvoiceAmount(inv.amount, inv.currency)}</strong>
                      </td>
                      <td>
                        <Badge color="success" className="sendgrid-billing__invoice-badge">
                          {formatInvoiceStatus(inv.status)}
                        </Badge>
                      </td>
                      <td>
                        {inv.pdfUrl ? (
                          <a
                            href={inv.pdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="small"
                          >
                            {intl.formatMessage({
                              id: "app.sendgridBilling.invoices.download",
                            })}
                          </a>
                        ) : (
                          <span className="sendgrid-billing__invoice-pdf-status">
                            {formatPdfStatus(inv.pdfStatus, inv.pdfUrl)}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
